import React, { useState } from "react";
import { intl } from "LocaleWrapper";

import { Button, FormGroup, Select } from "@cofano/cck";

import { CustomDialog } from "components/dialogs/Dialogs";
import AgreementTemplateSelect, { AgreementTemplateSelectOption } from "components/selects/AgreementTemplateSelect";

import { Agreement } from "types/index";
import { CharterSubtype, CharterType } from "__generated__/enums";

type AgreementTemplatesDialogProps = {
	open: boolean;
	onClose: () => void;
	onAddAgreements: (agreements: Agreement[]) => void;
	onOverwriteAgreements: (agreements: Agreement[]) => void;
	compatibleCharterType: CharterType;
	compatibleCharterSubtype: CharterSubtype;
};

const AgreementTemplatesDialog = ({
	open,
	onClose,
	onAddAgreements,
	onOverwriteAgreements,
	compatibleCharterType,
	compatibleCharterSubtype,
}: AgreementTemplatesDialogProps) => {

	const [ selectedTemplate, selectTemplate ] = useState<AgreementTemplateSelectOption | null>(null);
	const [ selectedAgreements, selectAgreements ] = useState<Agreement[] | null>(null);

	const handleOnClose = () => {
		onClose();
		selectTemplate(null);
		selectAgreements(null);
	};

	// @TO DO
	// Create copy sale or purchase agreements when the new contract page is used on the dossier page

	return (
		<CustomDialog
			open={open}
			onClose={onClose}
			title={intl.formatMessage({ id: "TEMPLATES_TITLE" })}
			actions={(
				<React.Fragment>
					<Button
						disabled={!selectedAgreements}
						onClick={() => {
							onAddAgreements(selectedAgreements || []);
							handleOnClose();
						}}
						size="small">
						{intl.formatMessage({ id: "AGREEMENT_ADD" })}
					</Button>

					<Button
						disabled={!selectedAgreements}
						onClick={() => {
							onOverwriteAgreements(selectedAgreements ? selectedAgreements : []);
							handleOnClose();
						}}
						size="small">
						{intl.formatMessage({ id: "OVERWRITE" })}
					</Button>
				</React.Fragment>
			)}>
			<React.Fragment>
				<FormGroup label={intl.formatMessage({ id: "TEMPLATE" })} overflow="hidden" tooltip={intl.formatMessage({ id: "AGREEMENT_TEMPLATE_COMPATIBILITY" })}>
					<AgreementTemplateSelect
						value={selectedTemplate}
						onChange={option  => {
							selectTemplate(option);
							selectAgreements(option?.agreements ? option.agreements as Agreement[] : null);
						}}
						charterType={compatibleCharterType}
						charterSubtype={compatibleCharterSubtype}
					/>
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "AGREEMENTS" })} overflow="hidden" required>
					<Select
						value={selectedAgreements}
						onChange={(option: Agreement[] | null) => selectAgreements(option)}
						fetchOptions={selectedTemplate?.agreements ? selectedTemplate.agreements as Agreement[] : []}
						getOptionLabel={(option: Agreement) => (option.description || option.type) as string}
						renderOption={(props, option: Agreement) => (
							<li {...props}>
								<div>
									<div>{option.description || option.type}</div>
									<div>
										<small>{option.summary}</small>
									</div>
								</div>
							</li>
						)}
						multiple />
				</FormGroup>
			</React.Fragment>
		</CustomDialog>
	);
};

export default AgreementTemplatesDialog;
